import Arrow from '@assets/svg/icons/arrow.svg'
import { SiteSeo } from '@blocks/site-seo'
import { ContentType } from '@entries/content-types'
import ContentfulSeo from '@entries/contentful-seo'
import { useSubscriptionLinkProps } from '@hooks/links/use-subscription-link-props'
import { Navbar } from '@layouts/navbar/navbar'
import { SEO_IDS } from '@meta/constants'
import { MetaUrl } from '@meta/meta-url'
import { Entry } from '@resolvers/entry'
import Button from '@ui/atoms/button'
import { mapNodesToObject } from '@utils/map-nodes-to-object'
import clsx from 'clsx/lite'
import { graphql, type HeadFC, type PageProps, Slice } from 'gatsby'
import { GatsbyImage, getImage } from 'gatsby-plugin-image'
import React, { type FC, useMemo } from 'react'

import Hero from './_hero'

const HubLivretPage: FC<PageProps<Queries.HubLivretPageAssetsQuery>> = ({
  data: {
    staticPage: { banner },
    backgroundImages,
  },
}) => {
  const backgroundImagesObj =
    mapNodesToObject<Queries.ContentfulFullWidthAvifAssetFragment>(
      backgroundImages.nodes
    )
  const aSavingsSubscriptionLinkProps = useSubscriptionLinkProps(
    '/epargne/livret/ouvrir-un-livret/deja-client?product=a-savings-account'
  )
  const plusSavingsSubscriptionLinkProps = useSubscriptionLinkProps(
    '/epargne/livret/ouvrir-un-livret/choix-livret?product=plus-savings-account'
  )
  const lddSavingsSubscriptionLinkProps = useSubscriptionLinkProps(
    '/epargne/livret/ouvrir-un-livret/deja-client?product=eco-savings-account'
  )
  const youthSavingsSubscriptionLinkProps = useSubscriptionLinkProps(
    '/epargne/livret/ouvrir-un-livret/deja-client?product=youth-savings-account'
  )

  const links = useMemo(
    () => [
      {
        title: 'Livret A',
        description: 'Un livret simple et net',
        backgroundImage: backgroundImagesObj['5YH2a1RtkplSMyyfJ3VVp3'],
        href: aSavingsSubscriptionLinkProps,
      },
      {
        title: 'Livret +',
        description:
          'Un livret aux plafonds étendus pour maximiser votre capacité d’épargne',
        backgroundImage: backgroundImagesObj['3USyx1Aprw7U46IV4kEPLc'],
        href: plusSavingsSubscriptionLinkProps,
      },
      {
        title: 'LDDS',
        description: 'Une solution d’épargne engagée',
        className: 'bg-[#00350015] text-black-50!',
        href: lddSavingsSubscriptionLinkProps,
      },
      {
        title: 'Livret + Enfant',
        description: 'Une épargne qui grandit avec eux',
        backgroundImage: backgroundImagesObj.j6VhcMIGolQxrvxw9ZrKs,
        href: youthSavingsSubscriptionLinkProps,
      },
    ],
    [backgroundImages]
  )

  return (
    <>
      <Navbar isHiddenCTA isLightMode informationBanner={banner} />
      <Hero
        backgroundImage={backgroundImagesObj['1PhibiZThiOBPtyEhdiv5e']}
        subtitle="Grâce à nos différents livrets vous épargnez à votre rythme."
        title="C’est parti pour épargner !"
      />
      <div className="section-content-layout p-default text-amplification-black-premium tablet:flex-row tablet:gap-4 flex flex-col flex-wrap items-center justify-center gap-8 text-center">
        {links.map((link) => (
          <div
            key={link.title}
            className={clsx(
              'text-white-50 tablet:w-[48%] laptop:h-[420px] laptop:max-w-[550px] grid h-[260px] w-full max-w-[344px] rounded-[32px]',
              link.className
            )}
          >
            {link.backgroundImage && (
              <GatsbyImage
                alt=""
                className="grid-area-1-1 flex h-full w-full overflow-hidden rounded-[32px]"
                image={getImage(link.backgroundImage)}
              />
            )}
            <div className="grid-area-1-1 z-10 flex flex-col items-center justify-center">
              <h2 className="laptop:text-[72px] mb-2 text-[42px] font-medium">
                {link.title}
              </h2>
              <p className="laptop:px-14 laptop:text-[24px] px-10 text-[16px]">
                {link.description}
              </p>
              <Button
                className="laptop:h-[72px] laptop:w-[72px] mt-8 flex h-[45px] w-[45px] items-center justify-center rounded-full p-0!"
                {...link.href}
              >
                <Arrow className="fill-black-50 laptop:scale-150 rotate-180" />
              </Button>
            </div>
          </div>
        ))}
      </div>
      <Slice alias="footer" />
    </>
  )
}

export const Head: HeadFC<Queries.HubLivretPageAssetsQuery> = ({
  data: {
    staticPage: { seoRef: seo },
  },
}) => (
  <>
    <SiteSeo />
    <MetaUrl />
    <meta content="website" id={SEO_IDS.OG_TYPE} name="og:type" />
    <Entry
      content={seo}
      mapping={{
        [ContentType.ContentfulSeo]: ContentfulSeo,
      }}
    />
  </>
)

export const query = graphql`
  query HubLivretPageAssets {
    staticPage: contentfulStaticPage(
      slug: { eq: "/ouvrir/livret" }
      metadata: {
        tags: { elemMatch: { contentful_id: { eq: "scopeWebsite" } } }
      }
    ) {
      seoRef {
        ...ContentfulSeo
      }
      banner: getBanner {
        ...StaticPageInformationBanner
      }
    }
    backgroundImages: allContentfulAsset(
      filter: {
        metadata: {
          tags: { elemMatch: { contentful_id: { eq: "scopeWebsite" } } }
        }
        contentful_id: {
          in: [
            "1PhibiZThiOBPtyEhdiv5e"
            "5YH2a1RtkplSMyyfJ3VVp3"
            "3USyx1Aprw7U46IV4kEPLc"
            "j6VhcMIGolQxrvxw9ZrKs"
          ]
        }
      }
    ) {
      nodes {
        ...ContentfulAvifAsset
      }
    }
  }
`

export default HubLivretPage
